import React, { useState, useEffect } from 'react';
import { Tabs, Card, Button, Table, Tag, Space, Modal, Form, Input, Select, DatePicker, message, Statistic, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, WalletOutlined, DollarOutlined, BarChartOutlined } from '@ant-design/icons';
import { incomeService, IncomeSource, SourceWallet, IncomeTransaction, IncomeReport } from '../../../services/incomeService';
import { formatCurrency } from '../../../utils/formatters';

const { TabPane } = Tabs;
const { Option } = Select;

const IncomePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('sources');
  const [sources, setSources] = useState<IncomeSource[]>([]);
  const [wallets, setWallets] = useState<SourceWallet[]>([]);
  const [transactions, setTransactions] = useState<IncomeTransaction[]>([]);
  const [report, setReport] = useState<IncomeReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState<'source' | 'wallet' | 'transaction'>('source');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();

  const familyId = 'default'; // TODO: Get from context

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'sources':
          const sourcesData = await incomeService.getSources(familyId);
          setSources(sourcesData);
          break;
        case 'wallets':
          const walletsData = await incomeService.getWallets(familyId);
          setWallets(walletsData);
          break;
        case 'transactions':
          const transactionsData = await incomeService.getTransactions({ family_id: familyId });
          setTransactions(transactionsData);
          break;
        case 'reports':
          const reportData = await incomeService.getReport({ family_id: familyId });
          setReport(reportData);
          break;
      }
    } catch (error) {
      message.error('Lỗi khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (type: 'source' | 'wallet' | 'transaction') => {
    setModalType(type);
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (item: any, type: 'source' | 'wallet' | 'transaction') => {
    setModalType(type);
    setEditingItem(item);
    form.setFieldsValue(item);
    setModalVisible(true);
  };

  const handleDelete = async (id: number, type: 'source' | 'wallet') => {
    try {
      if (type === 'source') {
        await incomeService.deleteSource(id);
      }
      message.success('Xóa thành công');
      loadData();
    } catch (error) {
      message.error('Lỗi khi xóa');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingItem) {
        if (modalType === 'source') {
          await incomeService.updateSource(editingItem.id, values);
        } else if (modalType === 'wallet') {
          await incomeService.updateWallet(editingItem.id, values);
        }
        message.success('Cập nhật thành công');
      } else {
        if (modalType === 'source') {
          await incomeService.createSource({ ...values, family_id: familyId });
        } else if (modalType === 'wallet') {
          await incomeService.createWallet({ ...values, family_id: familyId });
        } else if (modalType === 'transaction') {
          await incomeService.createTransaction({ ...values, family_id: familyId });
        }
        message.success('Tạo thành công');
      }
      setModalVisible(false);
      loadData();
    } catch (error) {
      message.error('Lỗi khi lưu');
    }
  };

  // Columns cho bảng nguồn thu nhập
  const sourceColumns = [
    {
      title: 'Tên nguồn',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: IncomeSource) => (
        <Space>
          <span style={{ fontSize: '18px' }}>{record.icon}</span>
          <span style={{ color: record.color, fontWeight: 'bold' }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (text: string, record: IncomeSource) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record, 'source')}
          >
            Sửa
          </Button>
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id, 'source')}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ];

  // Columns cho bảng ví nguồn
  const walletColumns = [
    {
      title: 'Tên ví',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: SourceWallet) => (
        <Space>
          <span style={{ fontSize: '18px' }}>{record.source_icon}</span>
          <span style={{ fontWeight: 'bold' }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Nguồn thu nhập',
      dataIndex: 'source_name',
      key: 'source_name',
      render: (text: string, record: SourceWallet) => (
        <Tag color={record.source_color}>{text}</Tag>
      ),
    },
    {
      title: 'Số dư hiện tại',
      dataIndex: 'current_balance',
      key: 'current_balance',
      render: (value: number) => (
        <span style={{ fontWeight: 'bold', color: '#10B981' }}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (text: string, record: SourceWallet) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record, 'wallet')}
          >
            Sửa
          </Button>
        </Space>
      ),
    },
  ];

  // Columns cho bảng giao dịch
  const transactionColumns = [
    {
      title: 'Ngày',
      dataIndex: 'income_date',
      key: 'income_date',
      render: (date: string) => new Date(date).toLocaleDateString('vi-VN'),
    },
    {
      title: 'Loại',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      render: (type: string) => {
        const typeMap = {
          income: { text: 'Thu nhập', color: '#10B981' },
          expense: { text: 'Chi tiêu', color: '#EF4444' },
          transfer: { text: 'Chuyển khoản', color: '#3B82F6' },
        };
        const config = typeMap[type as keyof typeof typeMap];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Nguồn',
      dataIndex: 'source_name',
      key: 'source_name',
      render: (text: string, record: IncomeTransaction) => (
        <Space>
          <span>{record.source_icon}</span>
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Ví',
      dataIndex: 'wallet_name',
      key: 'wallet_name',
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (value: number, record: IncomeTransaction) => (
        <span style={{ 
          fontWeight: 'bold', 
          color: record.transaction_type === 'income' ? '#10B981' : '#EF4444' 
        }}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
    },
  ];

  const renderModalContent = () => {
    switch (modalType) {
      case 'source':
        return (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item name="name" label="Tên nguồn" rules={[{ required: true }]}>
              <Input placeholder="Nhập tên nguồn thu nhập" />
            </Form.Item>
            <Form.Item name="icon" label="Icon">
              <Input placeholder="💰" />
            </Form.Item>
            <Form.Item name="color" label="Màu sắc">
              <Input placeholder="#10B981" />
            </Form.Item>
            <Form.Item name="description" label="Mô tả">
              <Input.TextArea placeholder="Mô tả nguồn thu nhập" />
            </Form.Item>
          </Form>
        );
      case 'wallet':
        return (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item name="source_id" label="Nguồn thu nhập" rules={[{ required: true }]}>
              <Select placeholder="Chọn nguồn thu nhập">
                {sources.map(source => (
                  <Option key={source.id} value={source.id}>
                    <Space>
                      <span>{source.icon}</span>
                      <span>{source.name}</span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="name" label="Tên ví" rules={[{ required: true }]}>
              <Input placeholder="Nhập tên ví" />
            </Form.Item>
            <Form.Item name="initial_balance" label="Số dư ban đầu">
              <Input type="number" placeholder="0" />
            </Form.Item>
            <Form.Item name="description" label="Mô tả">
              <Input.TextArea placeholder="Mô tả ví" />
            </Form.Item>
          </Form>
        );
      case 'transaction':
        return (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item name="source_id" label="Nguồn thu nhập" rules={[{ required: true }]}>
              <Select placeholder="Chọn nguồn thu nhập">
                {sources.map(source => (
                  <Option key={source.id} value={source.id}>
                    <Space>
                      <span>{source.icon}</span>
                      <span>{source.name}</span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="wallet_id" label="Ví nguồn" rules={[{ required: true }]}>
              <Select placeholder="Chọn ví">
                {wallets.map(wallet => (
                  <Option key={wallet.id} value={wallet.id}>
                    <Space>
                      <span>{wallet.source_icon}</span>
                      <span>{wallet.name}</span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="amount" label="Số tiền" rules={[{ required: true }]}>
              <Input type="number" placeholder="Nhập số tiền" />
            </Form.Item>
            <Form.Item name="transaction_type" label="Loại giao dịch" rules={[{ required: true }]}>
              <Select placeholder="Chọn loại giao dịch">
                <Option value="income">Thu nhập</Option>
                <Option value="expense">Chi tiêu</Option>
                <Option value="transfer">Chuyển khoản</Option>
              </Select>
            </Form.Item>
            <Form.Item name="income_date" label="Ngày giao dịch" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="notes" label="Ghi chú">
              <Input.TextArea placeholder="Ghi chú giao dịch" />
            </Form.Item>
          </Form>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ marginBottom: '24px' }}>Quản Lý Thu Nhập</h1>
      
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <span>
              <DollarOutlined />
              Nguồn Thu Nhập
            </span>
          } 
          key="sources"
        >
          <Card
            title="Danh sách nguồn thu nhập"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreate('source')}>
                Thêm nguồn
              </Button>
            }
          >
            <Table
              columns={sourceColumns}
              dataSource={sources}
              loading={loading}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <WalletOutlined />
              Ví Nguồn
            </span>
          } 
          key="wallets"
        >
          <Card
            title="Danh sách ví nguồn"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreate('wallet')}>
                Thêm ví
              </Button>
            }
          >
            <Table
              columns={walletColumns}
              dataSource={wallets}
              loading={loading}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <DollarOutlined />
              Giao Dịch
            </span>
          } 
          key="transactions"
        >
          <Card
            title="Lịch sử giao dịch"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreate('transaction')}>
                Thêm giao dịch
              </Button>
            }
          >
            <Table
              columns={transactionColumns}
              dataSource={transactions}
              loading={loading}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <BarChartOutlined />
              Báo Cáo
            </span>
          } 
          key="reports"
        >
          <Card title="Báo cáo thu nhập">
            {report && (
              <>
                <Row gutter={16} style={{ marginBottom: '24px' }}>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Tổng thu nhập"
                        value={report.incomeBySource.reduce((sum, item) => sum + item.total_amount, 0)}
                        precision={0}
                        valueStyle={{ color: '#10B981' }}
                        suffix="₫"
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Tổng chi tiêu"
                        value={report.expenseByWallet.reduce((sum, item) => sum + item.total_expense, 0)}
                        precision={0}
                        valueStyle={{ color: '#EF4444' }}
                        suffix="₫"
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Số dư tổng"
                        value={report.walletBalances.reduce((sum, item) => sum + item.current_balance, 0)}
                        precision={0}
                        valueStyle={{ color: '#3B82F6' }}
                        suffix="₫"
                      />
                    </Card>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Card title="Thu nhập theo nguồn">
                      {report.incomeBySource.map((item, index) => (
                        <div key={index} style={{ marginBottom: '12px' }}>
                          <Space>
                            <span style={{ fontSize: '18px' }}>{item.icon}</span>
                            <span style={{ fontWeight: 'bold' }}>{item.name}</span>
                            <span style={{ color: '#10B981', fontWeight: 'bold' }}>
                              {formatCurrency(item.total_amount)}
                            </span>
                          </Space>
                        </div>
                      ))}
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="Số dư các ví">
                      {report.walletBalances.map((item, index) => (
                        <div key={index} style={{ marginBottom: '12px' }}>
                          <Space>
                            <span style={{ fontSize: '18px' }}>{item.source_icon}</span>
                            <span style={{ fontWeight: 'bold' }}>{item.name}</span>
                            <span style={{ color: '#3B82F6', fontWeight: 'bold' }}>
                              {formatCurrency(item.current_balance)}
                            </span>
                          </Space>
                        </div>
                      ))}
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Card>
        </TabPane>
      </Tabs>

      <Modal
        title={
          editingItem 
            ? `Sửa ${modalType === 'source' ? 'nguồn thu nhập' : modalType === 'wallet' ? 'ví' : 'giao dịch'}`
            : `Thêm ${modalType === 'source' ? 'nguồn thu nhập' : modalType === 'wallet' ? 'ví' : 'giao dịch'}`
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        {renderModalContent()}
      </Modal>
    </div>
  );
};

export default IncomePage; 